import moment from "@/date";
import { Moment } from "moment";
import { LanguageCode } from "@/i18n";

// Decode a JWT string returning a Javascript object
export const jwtDecode = (token: string): Record<string, any> => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace("-", "+").replace("_", "/");
  return JSON.parse(window.atob(base64));
};

// This function removes unused authentication tokens from local storage.
export const purgeTokens = (): void => {
  for (var key in localStorage) {
    if (key.includes("vue-authenticate")) {
      localStorage.removeItem(key);
    }
  }
};

// Parse and download response returning a CSV payload.
export const downloadCsv = (csv: string, prefix: string, isFiltered: boolean = false): void => {
  const dateFormat = "YYYY-MM-DD";
  const link = document.createElement("a");
  link.href = `data:text/csv;charset=utf-8,${encodeURI(csv)}`;
  link.target = "_blank";
  link.download =
    `${prefix}_${moment().format(dateFormat)}` + `${isFiltered ? "_filtered" : ""}.csv`;
  link.click();
};

export const CURRENCIES = ["SEK", "EUR", "USD", "GBP"];

export function formatDatesDuration(
  start_date: string,
  end_date: string,
  min_duration: { days?: number; months?: number; years?: number } = {},
  clampEndDateToToday: boolean = false,
  locale: string = LanguageCode.English
): string {
  const today = moment();
  const providedEnd = moment(end_date, "YYYY-MM-DD", true);

  if ((!providedEnd.isValid() || providedEnd.isAfter(today)) && clampEndDateToToday) {
    end_date = today.format("YYYY-MM-DD");
  }

  const duration = moment.duration(moment(end_date).diff(start_date));
  const totalMonths = Math.round(duration.asMonths());
  let years = Math.floor(totalMonths / 12);
  let months = totalMonths % 12;
  let days = Math.round(duration.asDays());

  if (min_duration.days && days < min_duration.days) {
    days = min_duration.days;
  }

  if (min_duration.months && months < min_duration.months) {
    months = min_duration.months;
    days = 0;
  }

  if (min_duration.years && years < min_duration.years) {
    years = min_duration.years;
    months = 0;
    days = 0;
  }

  let builder = [];
  if (years > 0) {
    builder.push(
      moment.localeData(locale).relativeTime(years, true, years === 1 ? "y" : "yy", false)
    );
  }
  if (months > 0) {
    builder.push(
      moment.localeData(locale).relativeTime(months, true, months === 1 ? "M" : "MM", false)
    );
  }
  if (years == 0 && months == 0) {
    builder.push(
      moment.localeData(locale).relativeTime(days, true, days === 1 ? "d" : "dd", false)
    );
  }

  return builder.join(" ");
}

export function computeDayDifference(date1: Date, date2: Date): number {
  floorDays(date1);
  floorDays(date2);
  const dayInMillisecond = 1000 * 60 * 60 * 24;
  return Math.abs(new Date(date1).getTime() - new Date(date2).getTime()) / dayInMillisecond;
}

export function addDays(date: Date, nbDays: number): Date {
  const newDate = new Date(date); // copy Date object
  newDate.setDate(newDate.getDate() + nbDays);
  return newDate;
}

export function isWeekendDay(date: Date): boolean {
  return date.getDay() === 0 || date.getDay() === 6;
}

export function isWeekendDayV2(date: Moment): boolean {
  return date.isoWeekday() === 6 || date.isoWeekday() === 7;
}

export function getDayDateRange(start_date: Date, end_date: Date): Array<Date> {
  floorDays(start_date);
  floorDays(end_date);
  const range: Array<Date> = [];
  let curr = start_date;
  while (curr <= end_date) {
    range.push(curr);
    curr = addDays(curr, 1);
  }
  return range;
}

export function getDayDateRangeV2(start_date: Moment, end_date: Moment): Array<Moment> {
  floorDaysV2(start_date);
  floorDaysV2(end_date);
  const range: Array<Moment> = [];
  let curr = start_date;
  while (curr <= end_date) {
    range.push(curr);
    curr = moment(curr);
    curr.add(1, "days");
  }
  return range;
}

function floorDays(date: Date): void {
  date.setHours(0, 0, 0, 0);
}

function floorDaysV2(date: Moment): void {
  date.set("hour", 0);
  date.set("minute", 0);
  date.set("second", 0);
}

export function getMonthQueryKey(date: string | Date): string {
  const d = new Date(date);
  return `${d.getFullYear()}.${d.getMonth() + 1}`;
}

/**
 * Error message display durations (in seconds)
 */
export const DEFAULT_ERROR_DURATION = 3;
export const SPECIFIC_ERROR_DURATION = 5;

/**
 * API error response structure
 */
export interface ApiError extends Record<string, unknown> {
  status?: number;
  message?: string;
  error?: string;
  errors?: Record<string, string | string[]> | string;
  data?: {
    message?: string;
    error?: string;
    errors?: Record<string, string | string[]> | string;
  };
}

/**
 * Result of API error message extraction
 */
export interface ApiErrorMessageResult {
  message: string;
  isSpecificError: boolean;
}

/**
 * Extracts a user-friendly error message from an API error response.
 * Handles multiple error formats commonly returned by backend APIs:
 * - error.data.message: Single error message string
 * - error.data.error: Alternative single error string
 * - error.data.errors: Validation errors (can be string or object with field errors)
 *
 * @param error - The API error object
 * @param defaultMessage - Fallback message if no specific error is found
 * @returns Object containing the error message and a flag indicating if it's a specific error
 */
export function extractApiErrorMessage(
  error: ApiError,
  defaultMessage: string = "An error occurred"
): ApiErrorMessageResult {
  const extractMessagesFromObject = (data: Record<string, unknown>): string[] => {
    const errorMessages: string[] = [];

    for (const value of Object.values(data)) {
      if (typeof value === "string") {
        errorMessages.push(value);
      } else if (Array.isArray(value)) {
        // Runtime validation: Ensures all array elements are strings even if API returns unexpected data.
        // While TypeScript types may indicate string[], runtime API payloads can differ.
        errorMessages.push(...value.filter((item): item is string => typeof item === "string"));
      }
    }

    return errorMessages;
  };

  // Check for error message in various formats
  if (error.data?.message) {
    return { message: error.data.message, isSpecificError: true };
  } else if (error.data?.error) {
    return { message: error.data.error, isSpecificError: true };
  } else if (error.data?.errors) {
    const errors = error.data.errors;

    // Handle string errors
    if (typeof errors === "string") {
      return { message: errors, isSpecificError: true };
    }

    // Handle validation errors object (e.g., { field: ["error1", "error2"] } or { field: "error" })
    if (typeof errors === "object") {
      const errorMessages = extractMessagesFromObject(errors);

      if (errorMessages.length > 0) {
        return { message: errorMessages.join(" "), isSpecificError: true };
      }
    }
  }

  // Handle 422 payloads where field errors are directly on data root, e.g. { base: ["..."] }.
  if (error.status === 422 && error.data && typeof error.data === "object") {
    const fieldErrors = Object.fromEntries(
      Object.entries(error.data).filter(([key]) => !["message", "error", "errors"].includes(key))
    );

    const errorMessages = extractMessagesFromObject(fieldErrors);
    if (errorMessages.length > 0) {
      return { message: errorMessages.join(" "), isSpecificError: true };
    }
  }

  // Handle payloads passed directly at root level (no `data` wrapper), e.g. { base: ["..."] }.
  // This is common when callers pass `error.response.data` directly to this function.
  if (typeof error === "object" && error !== null) {
    if (error.message) {
      return { message: error.message, isSpecificError: true };
    } else if (error.error) {
      return { message: error.error, isSpecificError: true };
    } else if (error.errors) {
      const errors = error.errors;

      if (typeof errors === "string") {
        return { message: errors, isSpecificError: true };
      }

      if (typeof errors === "object") {
        const errorMessages = extractMessagesFromObject(errors);
        if (errorMessages.length > 0) {
          return { message: errorMessages.join(" "), isSpecificError: true };
        }
      }
    }

    const rootFieldErrors = Object.fromEntries(
      Object.entries(error).filter(
        ([key]) => !["status", "data", "message", "error", "errors"].includes(key)
      )
    );

    const rootErrorMessages = extractMessagesFromObject(rootFieldErrors);
    if (rootErrorMessages.length > 0) {
      return { message: rootErrorMessages.join(" "), isSpecificError: true };
    }
  }

  return { message: defaultMessage, isSpecificError: false };
}
